import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Table,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { inventoryAPI } from '../services/api.ts';

const { Title, Text } = Typography;

interface SplitItem {
  key: string;
  udc_pallet?: string;
  udc_collo?: string;
  quantity: number;
}

interface StockSplitModalProps {
  visible: boolean;
  stockId: number | null;
  stockCode?: string;
  productDescription?: string;
  availableQuantity: number;
  onOk: () => void;
  onCancel: () => void;
}

const StockSplitModal: React.FC<StockSplitModalProps> = ({
  visible,
  stockId,
  stockCode,
  productDescription,
  availableQuantity,
  onOk,
  onCancel,
}) => {
  const [splits, setSplits] = useState<SplitItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      // Inizializza con una riga vuota
      setSplits([{
        key: '1',
        udc_pallet: undefined,
        udc_collo: undefined,
        quantity: 0
      }]);
    } else {
      setSplits([]);
    }
  }, [visible]);

  const addSplit = () => {
    const newKey = (splits.length + 1).toString();
    setSplits([...splits, {
      key: newKey,
      udc_pallet: undefined,
      udc_collo: undefined,
      quantity: 0
    }]);
  };

  const removeSplit = (key: string) => {
    if (splits.length <= 1) {
      message.warning('Deve esserci almeno una riga di suddivisione');
      return;
    }
    setSplits(splits.filter(split => split.key !== key));
  };

  const updateSplit = (key: string, field: keyof SplitItem, value: any) => {
    setSplits(splits.map(split =>
      split.key === key ? { ...split, [field]: value } : split
    ));
  };

  const getTotalQuantity = () => {
    return splits.reduce((sum, split) => sum + (split.quantity || 0), 0);
  };

  const getRemainingQuantity = () => {
    return availableQuantity - getTotalQuantity();
  };

  const validateSplits = (): boolean => {
    const totalQuantity = getTotalQuantity();

    // Validazione quantità totale
    if (totalQuantity <= 0) {
      message.error('La quantità totale deve essere maggiore di 0');
      return false;
    }

    if (totalQuantity > availableQuantity) {
      message.error(`La quantità totale (${totalQuantity}) supera la quantità disponibile (${availableQuantity})`);
      return false;
    }

    // Validazione singoli split
    for (let i = 0; i < splits.length; i++) {
      const split = splits[i];

      if (!split.udc_pallet && !split.udc_collo) {
        message.error(`Riga ${i + 1}: Specificare almeno un UDC (Pallet o Collo)`);
        return false;
      }

      if (!split.quantity || split.quantity <= 0) {
        message.error(`Riga ${i + 1}: La quantità deve essere maggiore di 0`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!stockId) return;

    if (!validateSplits()) {
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        splits: splits.map(split => ({
          udc_pallet: split.udc_pallet || null,
          udc_collo: split.udc_collo || null,
          quantity: split.quantity
        }))
      };

      const result = await inventoryAPI.splitStock(stockId, requestData);
      message.success(`Stock suddiviso con successo in ${result.created_stocks.length} UDC`);
      onOk();
    } catch (error: any) {
      console.error('Errore nella suddivisione dello stock:', error);
      message.error(error.response?.data?.detail || 'Errore nella suddivisione dello stock');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'UDC Pallet',
      dataIndex: 'udc_pallet',
      key: 'udc_pallet',
      width: 200,
      render: (value: string, record: SplitItem) => (
        <Input
          placeholder="Codice Pallet (opzionale)"
          value={value}
          onChange={(e) => updateSplit(record.key, 'udc_pallet', e.target.value)}
        />
      ),
    },
    {
      title: 'UDC Collo',
      dataIndex: 'udc_collo',
      key: 'udc_collo',
      width: 200,
      render: (value: string, record: SplitItem) => (
        <Input
          placeholder="Codice Collo (opzionale)"
          value={value}
          onChange={(e) => updateSplit(record.key, 'udc_collo', e.target.value)}
        />
      ),
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (value: number, record: SplitItem) => (
        <InputNumber
          min={1}
          max={availableQuantity}
          value={value}
          onChange={(val) => updateSplit(record.key, 'quantity', val || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 80,
      render: (record: SplitItem) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeSplit(record.key)}
          disabled={splits.length <= 1}
        />
      ),
    },
  ];

  const totalQuantity = getTotalQuantity();
  const remainingQuantity = getRemainingQuantity();
  const hasError = totalQuantity > availableQuantity;

  return (
    <Modal
      title={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>Suddividi Stock in UDC</Title>
          <Text type="secondary">{productDescription}</Text>
          {stockCode && <Text type="secondary">Stock Code: {stockCode}</Text>}
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={800}
      confirmLoading={loading}
      okText="Conferma Suddivisione"
      cancelText="Annulla"
    >
      <Divider />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          message={
            <Space direction="vertical" size="small">
              <Text strong>Quantità Disponibile: {availableQuantity}</Text>
              <Text>Quantità da Suddividere: <strong>{totalQuantity}</strong></Text>
              <Text type={hasError ? 'danger' : remainingQuantity === 0 ? 'success' : 'warning'}>
                Quantità Rimanente: <strong>{remainingQuantity}</strong>
              </Text>
            </Space>
          }
          type={hasError ? 'error' : remainingQuantity === 0 ? 'success' : 'info'}
        />

        <Table
          columns={columns}
          dataSource={splits}
          pagination={false}
          size="small"
          rowKey="key"
        />

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addSplit}
          block
        >
          Aggiungi UDC
        </Button>

        <Alert
          message="Nota: I nuovi stock erediteranno tutti i metadati dello stock originale (prodotto, cliente, fornitore, magazzino, costi). Lo stock code NON verrà ereditato."
          type="info"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default StockSplitModal;
